/**
 * Client for the `fundraising` service — event reads and registration.
 *
 * Active whenever VITE_FUNDRAISING_API_BASE_URL is set, which it is in
 * `.env.production`. Unsetting it sends reads and registration back to the
 * legacy API: one environment variable, no deploy of application code. That is
 * the cutover rollback, and it works only while the legacy services still
 * exist — i.e. until migration-plan.md Phase 5.
 *
 * Plain `fetch` rather than the Amplify client, because this API is not an
 * Amplify-registered endpoint and needs no signing — the read routes are public.
 */

import { formatAddress, formImageForEvent, imageForEvent } from 'api/eventPresentation';
import {
    CharityEvent,
    CharityEventPackage,
    PlacedHold,
    RegistrationRefusalCode,
    RegistrationRefused,
    RegistrationRequest,
} from 'api/types';
import { FUNDRAISING_API_BASE_URL } from 'services/env';

interface FundraisingPackage {
    id: string;
    name: string;
    description: string;
    price_cents: number;
    capacity: number;
    max_per_registration: number;
    display_order: number;
}

interface FundraisingEvent {
    id: string;
    name: string;
    description: string;
    starts_at: string;
    ends_at: string;
    registration_opens_at: string;
    registration_closes_at: string;
    location: string;
    address: {
        address_line_1: string;
        address_line_2: string | null;
        city: string;
        state: string;
        zipcode: string;
    };
    packages: FundraisingPackage[];
}

interface FundraisingAvailability {
    event_id: string;
    registration_is_open: boolean;
    is_sold_out: boolean;
    packages: {
        package_id: string;
        name: string;
        price_cents: number;
        max_per_registration: number;
        remaining: number;
        is_sold_out: boolean;
    }[];
}

const url = (path: string): string => {
    const base = FUNDRAISING_API_BASE_URL;
    if (!base) {
        throw new Error('VITE_FUNDRAISING_API_BASE_URL is not set');
    }
    return `${base.replace(/\/$/, '')}${path}`;
};

const request = async <T>(path: string): Promise<T> => {
    const response = await fetch(url(path));
    if (!response.ok) {
        throw new Error(`GET ${path} failed with ${response.status}`);
    }
    return (await response.json()) as T;
};

/** Cents to dollars, at the boundary and nowhere else. */
const toDollars = (cents: number): number => cents / 100;

const mapPackage = (eventPackage: FundraisingPackage, remaining?: number): CharityEventPackage => ({
    packageId: eventPackage.id,
    title: eventPackage.name,
    name: eventPackage.name,
    cost: toDollars(eventPackage.price_cents),
    description: eventPackage.description,
    remaining,
    maxPackageCount: eventPackage.max_per_registration,
});

export const mapFundraisingEvent = (
    event: FundraisingEvent,
    availability?: FundraisingAvailability,
): CharityEvent => {
    const remainingByPackage = new Map(
        (availability?.packages ?? []).map((entry) => [entry.package_id, entry.remaining]),
    );
    return {
        eventId: event.id,
        eventTitle: event.name,
        eventDate: event.starts_at,
        description: event.description,
        image: imageForEvent(event.name),
        formImage: formImageForEvent(event.name),
        location: event.location,
        address: formatAddress(event.address),
        registrationStart: event.registration_opens_at,
        registrationEnd: event.registration_closes_at,
        packages: event.packages.map((eventPackage) =>
            mapPackage(eventPackage, remainingByPackage.get(eventPackage.id)),
        ),
    };
};

export const fetchFundraisingEvents = async (): Promise<CharityEvent[]> => {
    const events = await request<FundraisingEvent[]>('/events');
    return events.map((event) => mapFundraisingEvent(event));
};

export const fetchFundraisingEventDetails = async (eventId: string): Promise<CharityEvent> => {
    // Two requests: the event, and what is left of it. Availability is derived
    // per request rather than stored on the event, so it is a separate resource.
    const [event, availability] = await Promise.all([
        request<FundraisingEvent>(`/events/${eventId}`),
        request<FundraisingAvailability>(`/events/${eventId}/availability`),
    ]);
    return mapFundraisingEvent(event, availability);
};

interface PlacedHoldResponse {
    hold_id: string;
    expires_at: string;
    amount_cents: number;
    client_secret: string;
}

interface RefusalBody {
    detail?: { code?: string; message?: string };
}

/**
 * Start a registration against the fundraising service.
 *
 * Three things the legacy request shape did differently, each of which the new
 * API rejects rather than tolerates:
 *
 * - The form submits a line for *every* package, with `qty: '0'` for the ones
 *   nobody chose. Those are filtered out here; the API requires `qty >= 1`.
 * - `qty` is a string from the Select and has to be an integer.
 * - The attendee's email was scraped server-side out of the first package's
 *   details and never verified (ADR-0007). It is now sent explicitly and
 *   validated, so it is read here — from the first attendee of the first chosen
 *   package, which is who the old code meant all along.
 */
export const placeFundraisingHold = async (
    registration: RegistrationRequest,
): Promise<PlacedHold> => {
    const lines = registration.packages
        .map((pkg) => ({ ...pkg, qty: Number(pkg.qty) }))
        .filter((pkg) => pkg.qty > 0)
        .map((pkg) => ({
            package_id: pkg.packageId,
            qty: pkg.qty,
            attendees: pkg.details,
        }));

    const attendeeEmail = lines[0]?.attendees[0]?.email;
    if (!attendeeEmail) {
        throw new RegistrationRefused('refused', 'No attendee email was provided.');
    }

    const response = await fetch(url(`/events/${registration.eventId}/holds`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attendee_email: attendeeEmail, lines }),
    });

    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as RefusalBody;
        throw new RegistrationRefused(
            (body.detail?.code ?? 'refused') as RegistrationRefusalCode,
            body.detail?.message ?? `Registration failed with ${response.status}`,
        );
    }

    const placed = (await response.json()) as PlacedHoldResponse;
    return {
        clientSecret: placed.client_secret,
        holdId: placed.hold_id,
        expiresAt: placed.expires_at,
        amountCents: placed.amount_cents,
    };
};
